import React, { useEffect, useState } from "react"
import styled from "styled-components"
import PropTypes from "prop-types"

import Link from "@airasia/phoenix-components/components/Link"
import Title from "@airasia/phoenix-components/components/Title"
import Text from "@airasia/phoenix-components/components/Text"

import { GTM_PROJECT_CATEGORY } from "../utils/constants"
import { getGtmUserType } from "../utils/functions"

const StyledText = styled.div`
  margin-bottom: 15px;
`

StyledText.displayName = "StyledText"

const LinkContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  margin: 0px 8px;
`

LinkContainer.displayName = "LinkContainer"

const LinkDiv = styled.div`
  width: 25%;
  margin: 10px 0px;
  @media screen and (max-width: 768px) {
    width: 50%;
  }
`

LinkDiv.displayName = "LinkDiv"

const ExpandToggleDiv = styled.div`
  display: ${props => (props.show ? "none" : "block")};
`

const ExpandedLinkContainer = styled.div`
  display: ${props => (props.show ? "block" : "none")};
`

const LinkWrapper = props => {
  const { links, linkProps, gtmConsumerName } = props

  const gtmUserType = getGtmUserType()

  const onLinkClick = label => {
    const obj = {
      event: "trackEventNoEcommerce",
      "eventDetails.category": `${GTM_PROJECT_CATEGORY} | Internal Linking Block | ${gtmConsumerName}`,
      "eventDetails.action": `click`,
      "eventDetails.label": `${GTM_PROJECT_CATEGORY} | ${gtmUserType} | ${label}`,
    }

    window.gtmDataLayer = window.gtmDataLayer || []
    window.gtmDataLayer.push(obj)
  }

  return (
    <LinkContainer>
      {links.map(value => {
        return (
          <LinkDiv key={value.label} onClick={() => onLinkClick(value.label)}>
            <Link href={value.href} {...linkProps}>
              {value.label}
            </Link>
          </LinkDiv>
        )
      })}
    </LinkContainer>
  )
}

const InternalLinkingBlock = props => {
  const {
    linkObjects,
    blockTitle,
    blockDescription,
    accessible,
    linkSize,
    linkColor,
    linkHoverColor,
    linkHoverUnderlined,
    linkUnderlined,
    linkTarget,
    blockTitleType,
    descriptionTextColor,
    translations,
  } = props

  const linkProps = {
    accessible,
    color: linkColor,
    size: linkSize,
    hoverColor: linkHoverColor,
    hoverUnderlined: linkHoverUnderlined,
    target: linkTarget,
    underlined: linkUnderlined,
  }

  const titleProps = {
    accessible,
    type: blockTitleType,
  }

  const [links] = useState(linkObjects)
  const [showMoreToggle, setShowMoreToggle] = useState(false)

  const gtmUserType = getGtmUserType()

  useEffect(() => {
    const obj = {
      event: "trackEventNoEcommerce",
      "eventDetails.category": `${GTM_PROJECT_CATEGORY} | Internal Linking Block | ${props.gtmConsumerName}`,
      "eventDetails.action": `view`,
      "eventDetails.label": `${GTM_PROJECT_CATEGORY} | ${gtmUserType}`,
    }

    if (typeof window !== "undefined") {
      window.gtmDataLayer = window.gtmDataLayer || []
      window.gtmDataLayer.push(obj)
    }
  }, [])

  return (
    <div>
      {blockTitle && (
        <StyledText>
          <Title {...titleProps}>{blockTitle}</Title>
        </StyledText>
      )}
      {blockDescription && (
        <StyledText>
          <Text accessible color={descriptionTextColor}>
            {blockDescription}
          </Text>
        </StyledText>
      )}
      <LinkWrapper
        links={links.slice(0, 16)}
        linkProps={linkProps}
        gtmConsumerName={props.gtmConsumerName}
      />
      {links.length >= 16 && (
        <>
          <ExpandToggleDiv show={showMoreToggle}>
            <LinkDiv>
              <Link
                target="callback"
                callback={() => setShowMoreToggle(true)}
                color="secondary"
              >
                {links.length - 16} {translations.blockExpandText} ↓
              </Link>
            </LinkDiv>
          </ExpandToggleDiv>
          <ExpandedLinkContainer show={showMoreToggle}>
            <LinkWrapper
              links={links.slice(16)}
              linkProps={linkProps}
              gtmConsumerName={props.gtmConsumerName}
            />
            <LinkDiv>
              <Link
                target="callback"
                callback={() => setShowMoreToggle(false)}
                color="secondary"
              >
                {translations.blockCompressText} ↑
              </Link>
            </LinkDiv>
          </ExpandedLinkContainer>
        </>
      )}
    </div>
  )
}

InternalLinkingBlock.propTypes = {
  linkObjects: PropTypes.arrayOf(PropTypes.object).isRequired,
  blockTitle: PropTypes.string,
  blockDescription: PropTypes.string,
  accessible: PropTypes.bool,

  linkColor: PropTypes.string,
  linkSize: PropTypes.string,
  linkHoverColor: PropTypes.string,
  linkHoverUnderlined: PropTypes.bool,
  linkUnderlined: PropTypes.bool,
  linkTarget: PropTypes.string,

  blockTitleType: PropTypes.string,

  descriptionTextColor: PropTypes.string,
}

InternalLinkingBlock.defaultProps = {
  accessible: false,

  linkColor: "primary",
  linkSize: "normal",
  linkHoverColor: "neutral",
  linkHoverUnderlined: true,
  linkTarget: "self",

  blockTitleType: "h2",

  descriptionTextColor: "greyDark",
}

export default InternalLinkingBlock
